<template>
    <div class="ChangeTicketCounterModal">
        <h1>Change Starting Number</h1>
        <div class="main">
            <FormNumber
                v-model="startingNumber"
                label="New Starting Number"
                placeholder="1000"
                :errors="$v.startingNumber.$error ? [
                    !$v.startingNumber.required && 'Starting number is required',
                ] : null"
            />

            <div class="alert">
                <div class="alertIcon">
                    <Icon name="exclamation"/>
                </div>
                <span>Warning! Once this number is changed, you cannot lower the starting number. Please choose your starting number carefully.</span>
            </div>
        </div>

        <div class="actions">
            <Button class="link" @click="handleCancel">Cancel</Button>
            <Button class="danger" @click="handleSubmit" :disabled="submitPending">Update Starting Number</Button>
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import { required } from 'vuelidate/lib/validators';

export default {
    name: 'ChangeTicketCounterModal',

    data: () => ({
        startingNumber: null,
        submitPending: false,
    }),

    props: {
        company: { type: Object, default: null },
        originalStartingNumber: { type: Number, default: null },
    },

    validations: {
        startingNumber: { required },
    },

    mounted() {
        this.startingNumber = this.originalStartingNumber;
    },

    methods: {
        isBase10MultipleOf10(num) {
            // Check if number is an integer
            if (!Number.isInteger(num)) {
                return false;
            }

            // Check if number is in base 10
            const base10Num = parseInt(num, 10);
            if (base10Num !== num) {
                return false;
            }

            // Check if number is a multiple of 10
            if (num % 10 !== 0) {
                return false;
            }

            // If all checks pass, return true
            return true;
        },

        async handleSubmit() {
            this.$v.$touch();
            if (this.$v.$invalid) return;

            if (this.submitPending) return;
            this.submitPending = true;

            const { startingNumber } = this;

            const isBaseMultiple = this.isBase10MultipleOf10(startingNumber);
            if (!isBaseMultiple) {
                this.submitPending = false;
                return this.$awn.alert('Number must be a multiple of 10 and not contain any decimals');
            }

            if (startingNumber === this.originalStartingNumber) {
                this.submitPending = false;
                return this.$awn.alert('Starting number must be different from the original starting number');
            }

            if (startingNumber < this.originalStartingNumber) {
                this.submitPending = false;
                return this.$awn.alert('Starting number must be greater than the original starting number');
            }

            const res = await api.updateTicketCounter(this.company.id, { startingNumber });
            this.submitPending = false;

            if (res.error) {
                return this.$awn.alert('Failed to update company ticket numbering');
            }

            this.$awn.success('Ticket numbering has been updated');
            this.$store.dispatch('company/fetchOverview');
            this.$emit('close');
        },

        handleCancel() {
            this.$emit('close');
        },
    },
}
</script>

<style lang="scss" scoped>
@import 'globals.scss';

.ChangeTicketCounterModal {
    @extend %modal;

    h1 {
        margin-bottom: 15px;
    }

    .actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 30px;
    }

    .alert {
        display: flex;
        align-items: center;
        margin-top: 10px;
        border-radius: $border-radius;
        overflow: hidden;
        background-color: rgba($yellow-color, 0.3);

        .alertIcon {
            padding: 10px 15px;
            display: flex;
            align-items: center;
            background-color: $yellow-color;
            color: #fff;
        }

        span {
            padding: 0 10px;
        }
    }

    &.delete .Button.primary {
        background-color: $red-color;
    }
}
</style>
